import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import communicationLogService from '../../../api/services/communicationLogService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import {
  ArrowLeft,
  Paperclip,
  FileText,
  Reply
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';

const ReplyCommunication = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [formData, setFormData] = useState({
    subject: '',
    type: 'email',
    priority: 'medium',
    to: '',
    cc: '',
    bcc: '',
    message: '',
    attachments: [],
    candidateId: '',
    offerId: '',
    scheduledDate: '',
    reminderDate: '',
    originalMessage: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for candidates and offers
  const candidates = [
    { id: 1, name: 'John Smith', email: 'john.smith@email.com', position: 'Software Engineer' },
    { id: 2, name: 'Sarah Johnson', email: 'sarah.johnson@email.com', position: 'Product Manager' },
    { id: 3, name: 'Mike Davis', email: 'mike.davis@email.com', position: 'UX Designer' },
    { id: 4, name: 'Emily Brown', email: 'emily.brown@email.com', position: 'Data Analyst' },
    { id: 5, name: 'David Wilson', email: 'david.wilson@email.com', position: 'DevOps Engineer' }
  ];

  const offers = [
    { id: 1, title: 'Software Engineer - Full Stack', candidate: 'John Smith', status: 'pending' },
    { id: 2, title: 'Product Manager - Senior', candidate: 'Sarah Johnson', status: 'accepted' },
    { id: 3, title: 'UX Designer - Lead', candidate: 'Mike Davis', status: 'negotiating' },
    { id: 4, title: 'Data Analyst - Junior', candidate: 'Emily Brown', status: 'pending' },
    { id: 5, title: 'DevOps Engineer - Senior', candidate: 'David Wilson', status: 'rejected' }
  ];

  const communicationTypes = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'sms', label: 'SMS' },
    { value: 'video_call', label: 'Video Call' }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low', color: 'text-gray-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  // Mock original communication data
  const mockOriginalCommunication = {
    id: parseInt(id),
    subject: 'Follow-up on Job Offer - Software Engineer Position',
    type: 'email',
    priority: 'high',
    from: 'john.smith@email.com',
    to: 'hr@company.com',
    cc: '',
    bcc: '',
    message: 'Dear HR Team,\n\nThank you for your email regarding the Software Engineer position. I am very interested in this opportunity and would like to discuss the details further.\n\nI am available for a call this week and would appreciate if we could schedule a time to discuss the next steps.\n\nBest regards,\nJohn Smith',
    attachments: [],
    candidateId: '1',
    offerId: '1',
    sentDate: '2024-01-10T14:30:00Z',
    createdBy: 'John Smith',
    status: 'read'
  };

  useEffect(() => {
    const loadOriginalCommunication = async () => {
      setIsLoading(true);
      try {
        const result = await communicationLogService.getCommunicationLogById(id);
        if (result && result.responseCode === 200) {
          const originalComm = result.data;
          
          // Set up reply data based on original communication
          setFormData({
            subject: `Re: ${originalComm.subject}`,
            type: originalComm.communicationType,
            priority: 'medium',
            to: originalComm.recipient, // Reply to the original sender
            cc: originalComm.cc || '',
            bcc: '',
            message: '',
            attachments: [],
            candidateId: originalComm.candidateId,
            offerId: originalComm.offerId,
            scheduledDate: '',
            reminderDate: '',
            originalMessage: originalComm.message
          });
        } else {
          showError('Failed to load original communication');
        }
      } catch (error) {
        showError('Error loading original communication. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadOriginalCommunication();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Apply validation based on field type
    if (name === 'subject') {
      // Only allow alphanumeric, spaces, and commas
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').substring(0, 150);
    } else if (name === 'message') {
      // Only allow alphanumeric, spaces, and commas
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').substring(0, 5000);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue || value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
    
    // Reset file input to allow selecting the same file again
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    
    // Reset file input to allow reattaching the same file
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.subject.trim())) {
      newErrors.subject = 'Subject can only contain alphanumeric characters, spaces, and commas.';
    } else if (formData.subject.length > 150) {
      newErrors.subject = 'Subject must be ≤ 150 characters.';
    }

    if (!formData.to.trim()) {
      newErrors.to = 'Recipient is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.message.trim())) {
      newErrors.message = 'Message can only contain alphanumeric characters, spaces, and commas.';
    } else if (formData.message.length > 5000) {
      newErrors.message = 'Message must be ≤ 5000 characters.';
    }

    if (formData.type === 'email' && formData.to && !/\S+@\S+\.\S+/.test(formData.to)) {
      newErrors.to = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the validation errors before submitting. All required fields must be filled correctly.');
      return;
    }
    
    // Check if all required fields are filled
    const requiredFields = ['subject', 'to', 'message'];
    const missingFields = requiredFields.filter(field => {
      return !formData[field] || formData[field].toString().trim() === '';
    });
    
    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const replyData = {
        message: formData.message,
        subject: formData.subject,
        priority: formData.priority
      };
      
      const result = await communicationLogService.replyToCommunication(id, replyData);
      
      if (result && result.responseCode === 201) {
        showSuccess('Reply sent successfully!');
        setTimeout(() => {
          navigate('/offer_communication_logs');
        }, 1500);
      } else {
        showError(result?.responseMessage || 'Failed to send reply');
      }
    } catch (error) {
      showError('Error sending reply. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/offer_communication_logs');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleCancel}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reply to Communication</h1>
            <p className="text-gray-600 mt-1">Reply to offer-related communication</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Reply className="h-4 w-4" />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
          >
            {isSubmitting ? 'Sending...' : 'Send Reply'}
          </Button>
        </div>
      </div>

      {/* Original Message */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <FileText className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Original Message</h3>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                From: {mockOriginalCommunication.from}
              </span>
              <span className="text-sm text-gray-500">
                {new Date(mockOriginalCommunication.sentDate).toLocaleString()}
              </span>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {mockOriginalCommunication.subject}
            </h4>
          </div>
          <div className="text-gray-700 whitespace-pre-wrap">
            {formData.originalMessage || 'Loading original message...'}
          </div>
        </div>
      </div>

      {/* Reply Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  // Only allow alphanumeric, spaces, and commas
                  if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={150}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.subject ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter reply subject"
                required
              />
              <div className="flex justify-between items-center mt-1">
                {errors.subject && (
                  <p className="text-sm text-red-600">{errors.subject}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.subject.length}/150 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {communicationTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Recipient Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From
              </label>
              <input
                type="text"
                value={user?.email || 'hr@company.com'}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                placeholder="Your email"
              />
              <p className="mt-1 text-xs text-gray-500">This is your email address</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To *
              </label>
              <input
                type="text"
                name="to"
                value={formData.to}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.to ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter recipient email or phone"
              />
              {errors.to && (
                <p className="mt-1 text-sm text-red-600">{errors.to}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Replying to: {mockOriginalCommunication.from}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {priorityLevels.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* CC and BCC */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC
              </label>
              <input
                type="text"
                name="cc"
                value={formData.cc}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter CC recipients"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                BCC
              </label>
              <input
                type="text"
                name="bcc"
                value={formData.bcc}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter BCC recipients"
              />
            </div>
          </div>

          {/* Related Records */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Candidate
              </label>
              <select
                name="candidateId"
                value={formData.candidateId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a candidate</option>
                {candidates.map(candidate => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name} - {candidate.position}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Offer
              </label>
              <select
                name="offerId"
                value={formData.offerId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select an offer</option>
                {offers.map(offer => (
                  <option key={offer.id} value={offer.id}>
                    {offer.title} - {offer.candidate}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Reply Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reply Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // Only allow alphanumeric, spaces, and commas
                if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={5000}
              rows={8}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.message ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter your reply message here..."
              required
            />
            <div className="flex justify-between items-center mt-1">
              {errors.message && (
                <p className="text-sm text-red-600">{errors.message}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.message.length}/5000 characters
              </p>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <Paperclip className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop files here, or click to select
                </p>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Add Files
                </label>
              </div>
              
              {formData.attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Attached Files:</h4>
                  <div className="space-y-2">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scheduling */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scheduled Date
              </label>
              <input
                type="datetime-local"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reminder Date
              </label>
              <input
                type="datetime-local"
                name="reminderDate"
                value={formData.reminderDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default ReplyCommunication;
